/** ログインAPIのレスポンス（Webサイトの /api/v1/login と同形式） */
export type DebugToken = {
  access_token: string,
  token_type: string,
  expires_in: string,
};

/** プラン選択で保存する起動情報 */
export type DebugPlan = {
  article_id: string,
  division_id: string,
  plan_id: string,
  plan_name: string,
  webcad_data_flag: number,
  lidar_data_flag: number,
};

const TOKEN_STORAGE_KEY = 'debug-access-token';
const PLAN_STORAGE_KEY = 'debug-plan';
const DEBUG_PARAM_KEYS = ['debug', 'debug_mode'];
const DEBUG_PARAM_VALUE = 'on';
// main.ts の localhost 分岐と同じ判定にする（applyTo の値はその分岐でしか補完されないため）
const LOCAL_URL_KEYWORD = 'localhost';
const BEARER_PREFIX = 'Bearer ';
const EMPTY_JSON = 'null';

/**
 * localhost の debug=on 時だけ使う、ログイントークンと選択プランの保管庫。
 * 本番の起動情報（bim_viewer）とは別キーに保存し、互いに干渉させない。
 */
export class DebugSession {
  /** URLに debug=on / debug_mode=on が付いているか（main.ts のデバッグ判定と合わせる） */
  public static isDebugMode(): boolean {
    const params = new URL(location.href).searchParams;
    return DEBUG_PARAM_KEYS.some((key) => params.get(key) === DEBUG_PARAM_VALUE);
  }

  /** ローカル開発環境かつデバッグモードか */
  public static isEnabled(): boolean {
    return location.href.indexOf(LOCAL_URL_KEYWORD) > -1 && DebugSession.isDebugMode();
  }

  /** applyTo で保存済みプランが起動情報に差し込まれる状態か */
  public static hasAppliedPlan(): boolean {
    return DebugSession.isEnabled() && DebugSession.getPlan() !== null;
  }

  /** 有効期限内のトークンを返す。期限切れなら破棄して null */
  public static getToken(): DebugToken | null {
    const token = DebugSession.read<DebugToken>(TOKEN_STORAGE_KEY);
    if (!token) return null;
    const expiresAt = Date.parse(token.expires_in);
    if (!Number.isNaN(expiresAt) && expiresAt <= Date.now()) {
      DebugSession.clearToken();
      return null;
    }
    return token;
  }

  public static saveToken(token: DebugToken): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(token));
  }

  public static clearToken(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  /** Authorization ヘッダー値。未ログインなら null */
  public static getAuthorization(): string | null {
    const token = DebugSession.getToken();
    return token ? `${BEARER_PREFIX}${token.access_token}` : null;
  }

  public static getPlan(): DebugPlan | null {
    return DebugSession.read<DebugPlan>(PLAN_STORAGE_KEY);
  }

  public static savePlan(plan: DebugPlan): void {
    localStorage.setItem(PLAN_STORAGE_KEY, JSON.stringify(plan));
  }

  public static clearPlan(): void {
    localStorage.removeItem(PLAN_STORAGE_KEY);
  }

  /**
   * 保存済みのトークン・プランを起動情報（main.ts の info）へ上書きする。
   * localhost 分岐は info を最優先で読むため、ここで差し込めば後続のURL生成にも反映される。
   */
  public static applyTo(info: { [key: string]: unknown }): void {
    if (!DebugSession.isEnabled()) return;

    const authorization = DebugSession.getAuthorization();
    if (authorization) info.access_token = authorization;

    const plan = DebugSession.getPlan();
    if (!plan) return;
    info.plan_id = plan.plan_id;
    info.article_id = plan.article_id;
    info.division_id = plan.division_id;
    info.plan_name = plan.plan_name;
    info.lidar_data_flag = String(plan.lidar_data_flag);
    // main.ts は通常分岐で webcad_data_flag、localhost 分岐で webcad_data_flg を読むため両方入れる
    info.webcad_data_flag = String(plan.webcad_data_flag);
    info.webcad_data_flg = String(plan.webcad_data_flag);
  }

  private static read<T>(key: string): T | null {
    try {
      return JSON.parse(localStorage.getItem(key) ?? EMPTY_JSON) as T | null;
    } catch (_) {
      return null;
    }
  }
}
